import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { Button, message } from '@/ui/compat/antd';
import {
  LogoutOutlined,
  ReloadOutlined,
  CalendarOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  CloseCircleOutlined,
  StopOutlined,
} from '@/ui/compat/icons';
import AmicaleValidationApi from '../../../api/AmicaleValidationApi';
import FactureApi from '../../../api/FactureApi';
import AmicaleBalanceCard from './AmicaleBalanceCard';
import AmicaleReservationsFilters, {
  INITIAL_FILTERS,
} from './AmicaleReservationsFilters';
import AmicaleReservationsTable from './AmicaleReservationsTable';
import { AMICALE_VALIDATION_TAB } from './AmicaleValidationProductTabs';

const formatTnd = (n) =>
  Number.isFinite(Number(n))
    ? Number(n).toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
    : '0,00';

const KpiCard = ({ icon, label, count, amount, color = '#1e3a8a' }) => (
  <div className='bg-white rounded-2xl border border-gray-100 shadow-sm p-4 flex items-center gap-3 flex-1 min-w-[170px]'>
    <div className='text-2xl' style={{ color }}>{icon}</div>
    <div>
      <p className='text-xs text-gray-500 mb-0.5'>{label}</p>
      <p className='text-base font-bold text-gray-800 leading-none'>{count}</p>
      {amount !== undefined && (
        <p className='text-xs text-gray-400 mt-0.5'>{formatTnd(amount)} TND</p>
      )}
    </div>
  </div>
);

const PAGE_SIZE_DEFAULT = 25;

const AmicaleValidationDashboard = ({ user, client, onLogout }) => {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const productType =
    searchParams.get('productType') || AMICALE_VALIDATION_TAB.HOTEL;

  const [filters, setFilters] = useState(INITIAL_FILTERS);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(PAGE_SIZE_DEFAULT);
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  const [factureByReservation, setFactureByReservation] = useState({});
  const [balance, setBalance] = useState(null);
  const [balanceLoading, setBalanceLoading] = useState(false);
  const [balanceError, setBalanceError] = useState(null);

  const queryParams = useMemo(
    () => ({
      ...filters,
      productType,
      limit: pageSize,
      offset: (page - 1) * pageSize,
    }),
    [filters, productType, page, pageSize]
  );

  useEffect(() => {
    setPage(1);
  }, [productType]);

  const loadBalance = useCallback(async () => {
    setBalanceLoading(true);
    setBalanceError(null);
    try {
      const data = await AmicaleValidationApi.getBalance();
      setBalance(data);
    } catch (err) {
      setBalanceError(
        err?.response?.data?.message || t('amicales.dashboard.balanceLoadError')
      );
    } finally {
      setBalanceLoading(false);
    }
  }, []);

  const loadReservations = useCallback(async () => {
    setLoading(true);
    try {
      const data = await AmicaleValidationApi.listReservations(queryParams);
      setRows(data?.items || []);
      setTotal(data?.total || 0);
      setStats(data?.stats || null);
    } catch (err) {
      message.error(err?.response?.data?.message || t('amicales.dashboard.loadError'));
    } finally {
      setLoading(false);
    }
  }, [queryParams]);

  const loadFactures = useCallback(async () => {
    if (!client?.id) return;
    try {
      const factures = await FactureApi.getFacturesByClient(client.id);
      
      const map = {};
      (factures.filter(f => f.type_facture === 'client') || []).forEach((f) => {
        if (f?.id_reservation) {
          map[`hotel:${f.id_reservation}`] = f;
        }
        if (f?.id_ca_reservation) {
          map[`ca:${f.id_ca_reservation}`] = f;
        }
      });
      setFactureByReservation(map);
    } catch {
      setFactureByReservation({});
    }
  }, [client?.id]);

  useEffect(() => {
    loadReservations();
  }, [loadReservations, refreshKey]);

  useEffect(() => {
    loadBalance();
  }, [loadBalance, refreshKey]);

  useEffect(() => {
    loadFactures();
  }, [loadFactures, refreshKey]);

  const handleFiltersChange = (next) => {
    setFilters(next);
    setPage(1);
  };

  const handleReset = () => {
    setFilters(INITIAL_FILTERS);
    setPage(1);
  };

  const handlePageChange = (p, ps) => {
    setPage(p);
    setPageSize(ps);
  };

  const handleRefresh = () => setRefreshKey((k) => k + 1);

  return (
    <div className='w-full max-w-[1400px] mx-auto px-3 md:px-6 py-6'>
      <div className='flex flex-col md:flex-row md:items-center md:justify-between gap-3 mb-5'>
        <div>
          <h1 className='text-xl md:text-2xl font-bold text-gray-900'>
            {t('amicales.dashboard.title', { name: client?.name || user?.name || 'Amicale' })}
          </h1>
          <p className='text-sm text-gray-500'>
            {t('amicales.dashboard.subtitle')}
          </p>
        </div>
        <div className='flex gap-2'>
          <Button icon={<ReloadOutlined />} onClick={handleRefresh}>
            {t('amicales.dashboard.refresh')}
          </Button>
          <Button danger icon={<LogoutOutlined />} onClick={onLogout}>
            {t('amicales.dashboard.logout')}
          </Button>
        </div>
      </div>

      <div className='mb-4'>
        <AmicaleBalanceCard
          balance={balance}
          loading={balanceLoading}
          error={balanceError}
        />
      </div>

      <div className='flex flex-wrap gap-3 mb-4'>
        <KpiCard
          icon={<CalendarOutlined />}
          label={t('amicales.dashboard.kpiTotal')}
          count={total}
          color='#1e3a8a'
        />
        <KpiCard
          icon={<CheckCircleOutlined />}
          label={t('amicales.dashboard.kpiValidated')}
          count={stats?.count_validated ?? '-'}
          amount={stats?.sum_validated}
          color='#16a34a'
        />
        <KpiCard
          icon={<ClockCircleOutlined />}
          label={t('amicales.dashboard.kpiPending')}
          count={stats?.count_pending ?? '-'}
          amount={stats?.sum_pending}
          color='#d97706'
        />
        <KpiCard
          icon={<CloseCircleOutlined />}
          label={t('amicales.dashboard.kpiRejected')}
          count={stats?.count_rejected ?? '-'}
          amount={stats?.sum_rejected}
          color='#dc2626'
        />
        <KpiCard
          icon={<StopOutlined />}
          label={t('amicales.dashboard.kpiCancelled')}
          count={stats?.count_cancelled ?? '-'}
          amount={stats?.sum_cancelled}
          color='#6b7280'
        />
      </div>

      <AmicaleReservationsFilters
        value={filters}
        onChange={handleFiltersChange}
        onReset={handleReset}
        productType={productType}
      />

      <div className='text-sm text-gray-500 mb-2 px-1'>
        {t(total === 1 ? 'amicales.dashboard.resultsCount' : 'amicales.dashboard.resultsCount_plural', { count: total })}
      </div>

      <AmicaleReservationsTable
        rows={rows}
        total={total}
        loading={loading}
        page={page}
        pageSize={pageSize}
        onPageChange={handlePageChange}
        voucherEmail={client?.voucherEmail}
        amicalClient={client}
        onRefresh={handleRefresh}
        factureByReservation={factureByReservation}
        clientId={client?.id}
        balance={balance}
        productType={productType}
      />
    </div>
  );
};

export default AmicaleValidationDashboard;
